/**
 * usePermissions Hook
 * Check user permissions from profile for RBAC
 */
import { useAppSelector } from '../store/hooks';

export const usePermissions = () => {
  // Get user profile from Redux store
  const profile = useAppSelector((state) => state.auth?.user);

  // Debug: Log profile to console (remove this after testing)
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 usePermissions - Profile:', profile);
    console.log('🔍 usePermissions - Is SuperUser:', profile?.is_superuser);
    console.log('🔍 usePermissions - Employee Profile:', profile?.employee_profile);
    console.log('🔍 usePermissions - Permissions:', profile?.employee_profile?.permissions);
  }

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permissionCode: string): boolean => {
    // SuperUsers have all permissions
    if (profile?.is_superuser) {
      return true;
    }

    // Check if employee has the permission
    const permissions = profile?.employee_profile?.permissions || [];
    return permissions.includes(permissionCode);
  };

  /**
   * Check if user has ANY of the given permissions
   */
  const hasAnyPermission = (permissionCodes: string[]): boolean => {
    if (profile?.is_superuser) {
      return true;
    }

    const permissions = profile?.employee_profile?.permissions || [];
    return permissionCodes.some(code => permissions.includes(code));
  };

  /**
   * Check if user has ALL of the given permissions
   */
  const hasAllPermissions = (permissionCodes: string[]): boolean => {
    if (profile?.is_superuser) {
      return true;
    }

    const permissions = profile?.employee_profile?.permissions || [];
    return permissionCodes.every(code => permissions.includes(code));
  };

  /**
   * Check if user is SuperUser
   */
  const isSuperUser = (): boolean => {
    return profile?.is_superuser || false;
  };

  /**
   * Get all user permissions
   */
  const getPermissions = (): string[] => {
    return profile?.employee_profile?.permissions || [];
  };

  /**
   * Get user role
   */
  const getRole = () => {
    return profile?.employee_profile?.role;
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSuperUser,
    getPermissions,
    getRole,
  };
};

